import { createApi } from "@reduxjs/toolkit/query/react";
import { createRetryingBaseQuery } from "../retryingBaseQuery";
import formatUuidT from "../../flatbuffers/display/uuid";

const baseQuery = createRetryingBaseQuery(process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE ?? "");

const resolveUuidString = (value: unknown): string =>
  formatUuidT(value as Parameters<typeof formatUuidT>[0]);

const resolveMaybeUuidOrRawString = (value: unknown): string => {
  const uuid = resolveUuidString(value);
  if (uuid) {
    return uuid;
  }

  if (typeof value === "string") {
    return value;
  }

  return "";
};

const withMapIdQuery = (url: string, mapId: string): string =>
  mapId ? `${url}?map_id=${encodeURIComponent(mapId)}` : url;

const resolveMapIdForWaypointMutation = (
  args: any,
  payloadSource: Record<string, unknown>,
  payload: Record<string, unknown>,
): string =>
  resolveMaybeUuidOrRawString(
    args?.map_id ??
      args?.mapId ??
      args?.body?.map_id ??
      args?.body?.mapId ??
      payloadSource.map_id ??
      payloadSource.mapId ??
      payload.map_id ??
      payload.mapId,
  );

const normalizeMapMutationBody = (body: Record<string, unknown>) => ({
  id: resolveUuidString(body.id),
  org_id: resolveUuidString(body.org_id ?? body.orgId),
  name: body.name ?? "",
  offline_url: body.offline_url ?? body.offlineUrl ?? "",
  map_img_url: body.map_img_url ?? body.mapImgUrl ?? "",
  has_display_view: body.has_display_view ?? body.hasDisplayView ?? false,
  is_main: body.is_main ?? body.isMain ?? body.main ?? false,
  waypoints: body.waypoints ?? body.waypoint ?? [],
  group_waypoints: body.group_waypoints ?? body.groupWaypoints ?? [],
  bank_waypoints: body.bank_waypoints ?? body.bankWaypoints ?? [],
});

const normalizeWaypointMutationBody = (body: Record<string, unknown>) => ({
  id: resolveUuidString(body.id),
  nickname: body.nickname ?? "",
  x: body.x ?? 0,
  y: body.y ?? 0,
  background_color_hex: body.background_color_hex ?? body.backgroundColorHex ?? "",
  background_opacity: body.background_opacity ?? body.backgroundOpacity ?? 0,
  background_width: body.background_width ?? body.backgroundWidth ?? 0,
  background_height: body.background_height ?? body.backgroundHeight ?? 0,
  display_name: body.display_name ?? body.displayName ?? true,
  font_size: body.font_size ?? body.fontSize ?? 0,
  font_color_hex: body.font_color_hex ?? body.fontColorHex ?? "",
  background_rotation_deg: body.background_rotation_deg ?? body.backgroundRotationDeg ?? 0,
  icon_rotation_deg: body.icon_rotation_deg ?? body.iconRotationDeg ?? 0,
  name_rotation_deg: body.name_rotation_deg ?? body.nameRotationDeg ?? 0,
  sync_rotations: body.sync_rotations ?? body.syncRotations ?? false,
  icon_width: body.icon_width ?? body.iconWidth ?? 0,
  icon_height: body.icon_height ?? body.iconHeight ?? 0,
  maintain_icon_aspect_ratio:
    body.maintain_icon_aspect_ratio ?? body.maintainIconAspectRatio ?? true,
  linked_lift: body.linked_lift ?? body.linkedLift ?? "",
});

const normalizeGroupWaypointMutationBody = (body: Record<string, unknown>) => ({
  id: resolveUuidString(body.id),
  nickname: body.nickname ?? "",
  x: body.x ?? 0,
  y: body.y ?? 0,
  background_color_hex: body.background_color_hex ?? body.backgroundColorHex ?? "",
  background_opacity: body.background_opacity ?? body.backgroundOpacity ?? 0,
  background_width: body.background_width ?? body.backgroundWidth ?? 0,
  background_height: body.background_height ?? body.backgroundHeight ?? 0,
  display_name: body.display_name ?? body.displayName ?? true,
  font_size: body.font_size ?? body.fontSize ?? 0,
  font_color_hex: body.font_color_hex ?? body.fontColorHex ?? "",
  background_rotation_deg: body.background_rotation_deg ?? body.backgroundRotationDeg ?? 0,
  name_rotation_deg: body.name_rotation_deg ?? body.nameRotationDeg ?? 0,
  sync_rotations: body.sync_rotations ?? body.syncRotations ?? false,
  min_x: body.min_x ?? body.minX ?? 0,
  max_x: body.max_x ?? body.maxX ?? 0,
  min_y: body.min_y ?? body.minY ?? 0,
  max_y: body.max_y ?? body.maxY ?? 0,
  lifts: body.lifts ?? body.lift_waypoints ?? body.liftWaypoints ?? [],
  banks: body.banks ?? body.bank_waypoints ?? body.bankWaypoints ?? [],
});

const normalizeBankWaypointMutationBody = (body: Record<string, unknown>) => ({
  id: resolveUuidString(body.id),
  nickname: body.nickname ?? "",
  bank_name: body.bank_name ?? body.bankName ?? "",
  x: body.x ?? 0,
  y: body.y ?? 0,
  background_color_hex: body.background_color_hex ?? body.backgroundColorHex ?? "",
  background_opacity: body.background_opacity ?? body.backgroundOpacity ?? 0,
  background_width: body.background_width ?? body.backgroundWidth ?? 0,
  background_height: body.background_height ?? body.backgroundHeight ?? 0,
  display_name: body.display_name ?? body.displayName ?? true,
  font_size: body.font_size ?? body.fontSize ?? 0,
  font_color_hex: body.font_color_hex ?? body.fontColorHex ?? "",
  background_rotation_deg: body.background_rotation_deg ?? body.backgroundRotationDeg ?? 0,
  name_rotation_deg: body.name_rotation_deg ?? body.nameRotationDeg ?? 0,
  sync_rotations: body.sync_rotations ?? body.syncRotations ?? false,
  min_x: body.min_x ?? body.minX ?? 0,
  max_x: body.max_x ?? body.maxX ?? 0,
  min_y: body.min_y ?? body.minY ?? 0,
  max_y: body.max_y ?? body.maxY ?? 0,
  link_lifts: body.link_lifts ?? body.linkLifts ?? true,
  lifts: body.lifts ?? body.lift_waypoints ?? body.liftWaypoints ?? [],
});

const resolveMutationId = (value: unknown): string => resolveUuidString(value);

const extractMutationPayload = (args: any): Record<string, unknown> => {
  if (!args || typeof args !== "object") {
    return {};
  }

  if (args.body && typeof args.body === "object" && !Array.isArray(args.body)) {
    return args.body as Record<string, unknown>;
  }

  return args as Record<string, unknown>;
};

export const mapViewAPI = createApi({
  reducerPath: "mapViewAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagGetMapView"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET MAP VIEWS */
    getMapView: builder.query({
      query: (body: any) => {
        const orgId = resolveUuidString(
          body?.body?.org_id ?? body?.body?.orgId ?? body?.org_id ?? body?.orgId,
        );
        const querySuffix = orgId ? `?org_id=${orgId}` : "";

        return {
          url: `/maps/${querySuffix}`,
          headers: { authorization: body?.token },
        };
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}-${resolveUuidString(queryArgs?.body?.org_id ?? queryArgs?.body?.orgId ?? queryArgs?.org_id ?? queryArgs?.orgId)}`,
      providesTags: ["tagGetMapView"],
    }),
    //* *********************************************************** */
    //* UPDATE MAP VIEW */
    putMapView: builder.mutation({
      query: (body: any) => {
        const payloadSource = extractMutationPayload(body);
        const payload = normalizeMapMutationBody(payloadSource);
        const mapId = resolveMutationId(
          body?.id ??
            payload.id ??
            payloadSource.id ??
            body?.body?.id ??
            body?.body?.map_id ??
            body?.body?.mapId,
        );

        return {
          url: `/maps/${mapId}`,
          method: "PUT",
          body: payload,
          headers: { authorization: body?.token ?? body?.headers?.authorization },
        };
      },
      extraOptions: { fbSerializer: "UpdateMap" },
      invalidatesTags: ["tagGetMapView"],
    }),
    //* *********************************************************** */
    //* DELETE MAP VIEW */
    deleteMapView: builder.mutation({
      query: (body: any) => ({
        url: `/maps/${body.id}`,
        method: "DELETE",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetMapView"],
    }),
    //* *********************************************************** */
    //* CREATE MAP VIEW */
    postMapView: builder.mutation({
      query: (body: any) => {
        const payloadSource = extractMutationPayload(body);
        const payload = normalizeMapMutationBody(payloadSource);

        return {
          url: "/maps/",
          method: "POST",
          body: payload,
          headers: { authorization: body?.token ?? body?.headers?.authorization },
        };
      },
      extraOptions: { fbSerializer: "CreateMap", maxRetries: 0 },
      invalidatesTags: ["tagGetMapView"],
    }),
    //* *********************************************************** */
    //* UPDATE MAP PINS */
    postMapViewPin: builder.mutation({
      query: (body: any) => {
        const payloadSource = extractMutationPayload(body);
        const payload = normalizeMapMutationBody(payloadSource);
        const mapId = resolveMutationId(
          body?.id ??
            payload.id ??
            payloadSource.id ??
            body?.body?.id ??
            body?.body?.map_id ??
            body?.body?.mapId,
        );

        return {
          url: `/maps/${mapId}`,
          method: "PUT",
          body: payload,
          headers: { authorization: body?.token ?? body?.headers?.authorization },
        };
      },
      extraOptions: { fbSerializer: "UpdateMap" },
      invalidatesTags: ["tagGetMapView"],
    }),
    //* *********************************************************** */
    //* WAYPOINT CRUD */
    postWaypoint: builder.mutation({
      query: (body: any) => {
        const payloadSource = extractMutationPayload(body);
        const payload = normalizeWaypointMutationBody(payloadSource);
        const mapId = resolveMapIdForWaypointMutation(body, payloadSource, payload);

        return {
          url: withMapIdQuery("/maps/waypoints", mapId),
          method: "POST",
          body: payload,
          headers: { authorization: body?.token ?? body?.headers?.authorization },
        };
      },
      extraOptions: { fbSerializer: "CreateWaypoint", maxRetries: 0 },
    }),
    putWaypoint: builder.mutation({
      query: (body: any) => {
        const payloadSource = extractMutationPayload(body);
        const payload = normalizeWaypointMutationBody(payloadSource);
        const waypointId = resolveMutationId(
          body?.id ?? payload.id ?? payloadSource.id ?? body?.body?.id,
        );
        const mapId = resolveMapIdForWaypointMutation(body, payloadSource, payload);

        return {
          url: withMapIdQuery(`/maps/waypoints/${waypointId}`, mapId),
          method: "PUT",
          body: payload,
          headers: { authorization: body?.token ?? body?.headers?.authorization },
        };
      },
      extraOptions: { fbSerializer: "UpdateWaypoint" },
    }),
    deleteWaypoint: builder.mutation({
      query: (body: any) => ({
        url: `/maps/waypoints/${resolveMutationId(body?.id ?? body?.waypointId)}`,
        method: "DELETE",
        headers: { authorization: body?.token ?? body?.headers?.authorization },
      }),
    }),
    //* *********************************************************** */
    //* GROUP WAYPOINT CRUD */
    postGroupWaypoint: builder.mutation({
      query: (body: any) => {
        const payloadSource = extractMutationPayload(body);
        const payload = normalizeGroupWaypointMutationBody(payloadSource);
        const mapId = resolveMapIdForWaypointMutation(body, payloadSource, payload);

        return {
          url: withMapIdQuery("/maps/groupWaypoints", mapId),
          method: "POST",
          body: payload,
          headers: { authorization: body?.token ?? body?.headers?.authorization },
        };
      },
      extraOptions: { fbSerializer: "CreateGroupWaypoint", maxRetries: 0 },
    }),
    putGroupWaypoint: builder.mutation({
      query: (body: any) => {
        const payloadSource = extractMutationPayload(body);
        const payload = normalizeGroupWaypointMutationBody(payloadSource);
        const groupWaypointId = resolveMutationId(
          body?.id ?? payload.id ?? payloadSource.id ?? body?.body?.id,
        );
        const mapId = resolveMapIdForWaypointMutation(body, payloadSource, payload);

        return {
          url: withMapIdQuery(`/maps/groupWaypoints/${groupWaypointId}`, mapId),
          method: "PUT",
          body: payload,
          headers: { authorization: body?.token ?? body?.headers?.authorization },
        };
      },
      extraOptions: { fbSerializer: "UpdateGroupWaypoint" },
    }),
    deleteGroupWaypoint: builder.mutation({
      query: (body: any) => ({
        url: `/maps/groupWaypoints/${resolveMutationId(body?.id ?? body?.groupWaypointId)}`,
        method: "DELETE",
        headers: { authorization: body?.token ?? body?.headers?.authorization },
      }),
    }),
    //* *********************************************************** */
    //* BANK WAYPOINT CRUD */
    postBankWaypoint: builder.mutation({
      query: (body: any) => {
        const payloadSource = extractMutationPayload(body);
        const payload = normalizeBankWaypointMutationBody(payloadSource);
        const mapId = resolveMapIdForWaypointMutation(body, payloadSource, payload);

        return {
          url: withMapIdQuery("/maps/bankWaypoints", mapId),
          method: "POST",
          body: payload,
          headers: { authorization: body?.token ?? body?.headers?.authorization },
        };
      },
      extraOptions: { fbSerializer: "CreateBankWaypoint", maxRetries: 0 },
    }),
    putBankWaypoint: builder.mutation({
      query: (body: any) => {
        const payloadSource = extractMutationPayload(body);
        const payload = normalizeBankWaypointMutationBody(payloadSource);
        const bankWaypointId = resolveMutationId(
          body?.id ?? payload.id ?? payloadSource.id ?? body?.body?.id,
        );
        const mapId = resolveMapIdForWaypointMutation(body, payloadSource, payload);

        return {
          url: withMapIdQuery(`/maps/bankWaypoints/${bankWaypointId}`, mapId),
          method: "PUT",
          body: payload,
          headers: { authorization: body?.token ?? body?.headers?.authorization },
        };
      },
      extraOptions: { fbSerializer: "UpdateBankWaypoint" },
    }),
    deleteBankWaypoint: builder.mutation({
      query: (body: any) => ({
        url: `/maps/bankWaypoints/${resolveMutationId(body?.id ?? body?.bankWaypointId)}`,
        method: "DELETE",
        headers: { authorization: body?.token ?? body?.headers?.authorization },
      }),
    }),
  }),
});

export const {
  useLazyGetMapViewQuery,
  usePostMapViewMutation,
  useDeleteMapViewMutation,
  usePutMapViewMutation,
  usePostMapViewPinMutation,
  usePostWaypointMutation,
  usePutWaypointMutation,
  useDeleteWaypointMutation,
  usePostGroupWaypointMutation,
  usePutGroupWaypointMutation,
  useDeleteGroupWaypointMutation,
  usePostBankWaypointMutation,
  usePutBankWaypointMutation,
  useDeleteBankWaypointMutation,
} = mapViewAPI;
